// Institute of Design and Innovation (email idi@unm.edu). //

import { AppointmentType, AppointmentTypeDetail } from '@/models/appointmentType'
import AppointmentTypeOffering from '@/models/appointmentTypeOffering'
import AvailabilitySlot from '@/models/availabilitySlot'
import IntakeForm from '@/models/intakeForm'
import { AxiosResponse } from 'axios'
import httpClient from './httpClient'

/**
 * Class containing routes for AppointmentType API
 */
export default class AppointmentTypeService {
  /**
   * Creates a new AppointmentType
   * @param AppointmentType The AppointmentType to be created
   */
  static create (form: AppointmentType): Promise<AxiosResponse<AppointmentType>> {
    return httpClient.post('/appointmentType', form)
  }

  /**
   * Creates a new Offering for the given AppointmentType
   * @param appointmentTypeId The Id of the AppoointmentType to create the Offering for
   * @param time The time of the appointment represented by the number of minutes since the start of day
   */
  static createOffering (appointmentTypeId: number, time: number) {
    return httpClient.post(`/appointmentType/${appointmentTypeId}/offering`, { hour: time })
  }

  /**
   * Returns the specified AppointmentType
   * @param id The Id of the AppointmentType
   */
  static get (id: number): Promise<AxiosResponse<AppointmentTypeDetail>> {
    return httpClient.get(`/appointmentType/${id}`)
  }

  /**
   * Returns all appointmentTypes
   */
  static getAll (): Promise<AxiosResponse<Array<AppointmentTypeDetail>>> {
    const params = {
      allInclusive: true
    }
    return httpClient.get('/appointmentType', { params: params })
  }

  /**
   * Returns all public appointmentTypes that are not tasks
   */
  static getPublic (): Promise<AxiosResponse<Array<AppointmentTypeDetail>>> {
    return httpClient.get('/appointmentType/public')
  }

  /**
   * Gets all available time slots for the given AppointmentType between the given dates
   * @param id The Id of the AppointmentType
   * @param start Lower Date Bound
   * @param end Upper Date Bound
   */
  static getAvailability (id: number, start: Date, end: Date, reviewerId = 0, includeUnavailable = false): Promise<AxiosResponse<Array<AvailabilitySlot>>> {
    const params = {
      start: start.toISOString(),
      end: end.toISOString(),
      reviewerId: reviewerId,
      includeUnavailable: String(includeUnavailable)
    }
    return httpClient.get(`/appointmentType/${id}/groupedAvailability`, { params: params })
  }

  /**
   * Removes the given AppointmentTypeOffering
   * @param id The Id of the AppoointmentTypeOffering to remove
   */
  static removeOffering (id: number): Promise<AxiosResponse<AppointmentTypeOffering>> {
    return httpClient.delete(`/appointmentType/offering/${id}`)
  }

  /**
   * Updates the given AppointmentType
   * @param form The AppointmentType to update
   */
  static update (form: AppointmentType): Promise<AxiosResponse<AppointmentType>> {
    return httpClient.put(`/appointmentType/${form.id}`, form)
  }

  /**
   * Updates the given AppointmentType's confirmation message
   * @param id The id of the appointment type
   * @param message The new message
   */
  static updateConfirmationMessage (id: number, message: string): Promise<AxiosResponse> {
    return httpClient.put(`/appointmentType/${id}/confirmationMessage`, { message: message })
  }

  /**
   * Updates an AppointmentType's IntakeForm
   * @param id The Id of the AppointmentType
   * @param form The IntakeForm to update
   */
  static updateIntakeForm (id: number, form: IntakeForm): Promise<AxiosResponse<IntakeForm>> {
    return httpClient.put(`/appointmentType/${id}/intakeForm`, form)
  }

  /**
   * Remove an AppointmentType, its offerings and any sponsors and user availabilities
   * @param id
   */
  static removeType (id: number): Promise<AxiosResponse<AppointmentType>> {
    return httpClient.put(`/appointmentType/remove/${id}`)
  }

  /**
   * Restore an AppointmentType and its offerings
   * @param id
   */
  static restoreType (id: number): Promise<AxiosResponse<AppointmentType>> {
    return httpClient.put(`/appointmentType/restore/${id}`)
  }

  /**
   * Updates the given offering
   * @param appointmenTypeId The If of the AppointmentType the offering belongs to
   * @param offering The offering to update
   */
  static updateOffering (appointmenTypeId: number, offering: AppointmentTypeOffering): Promise<AxiosResponse<AppointmentTypeOffering>> {
    return httpClient.post(`/appointmentType/${appointmenTypeId}/offering`, offering)
  }
}
